import {
  IBulkOfferingItem,
  IBulkQuoteOfferingReqSchema,
  IQuoteItemReqSchema,
  OfferingTypesEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { pluralize } from '@monetize/utils/core';
import isNil from 'lodash/isNil';
import { isNonProratedAdvanceProduct } from '../../../../../../utils/quotes';

export type BulkRateEntry = IBulkQuoteOfferingReqSchema['bulkRate'][number];
export type BulkOfferingProducts = NonNullable<
  IBulkOfferingItem['offeringProducts']
>;
type BulkOfferingProduct = BulkOfferingProducts[number];

export const areAllOfferingsOneTime = (offerings: IBulkOfferingItem[]) =>
  offerings.every(({ type }) => type === OfferingTypesEnum.ONETIME);

export const areAllBulkProductsOptional = (
  offeringProducts: BulkOfferingProducts = [],
) =>
  offeringProducts.length > 0 &&
  offeringProducts.every(({ isMandatory }) => !isMandatory);

/**
 * Products a bulk-added offering gets when the user does not choose: the
 * required ones, or the first product when every product is optional.
 */
export const getDefaultBulkProductIds = (
  offeringProducts: BulkOfferingProducts = [],
): string[] => {
  if (areAllBulkProductsOptional(offeringProducts)) {
    return [offeringProducts[0].product.id];
  }
  return offeringProducts
    .filter(({ isMandatory }) => isMandatory)
    .map(({ product }) => product.id);
};

/**
 * Every product that will become an item, in `offeringProducts` order.
 * Required products are always included, whatever `productIds` holds.
 */
export const getSelectedBulkProductIds = (
  offeringProducts: BulkOfferingProducts = [],
  productIds?: string[],
): Set<string> => {
  const chosenIds = new Set(
    productIds ?? getDefaultBulkProductIds(offeringProducts),
  );
  return new Set(
    offeringProducts
      .filter(
        ({ product, isMandatory }) => isMandatory || chosenIds.has(product.id),
      )
      .map(({ product }) => product.id),
  );
};

/**
 * A product's checkbox cannot change when the product is required, or when it
 * is the only one selected, so an offering never ends up with no items.
 */
export const isBulkProductLocked = (
  { product, isMandatory }: BulkOfferingProduct,
  selectedIds: Set<string>,
) => isMandatory || (selectedIds.has(product.id) && selectedIds.size === 1);

export const toggleBulkProductId = (
  offeringProducts: BulkOfferingProducts,
  productIds: string[] | undefined,
  productId: string,
  isChecked: boolean,
): string[] => {
  const selectedIds = getSelectedBulkProductIds(offeringProducts, productIds);
  if (isChecked) {
    selectedIds.add(productId);
  } else {
    selectedIds.delete(productId);
  }

  // Unchecking the last product is refused rather than leaving no items
  if (selectedIds.size === 0) {
    return [...getSelectedBulkProductIds(offeringProducts, productIds)];
  }

  return offeringProducts
    .filter(({ product }) => selectedIds.has(product.id))
    .map(({ product }) => product.id);
};

/**
 * Builds the quote items for one bulk-added offering. Quantities start at 0
 * and are replaced by the rate's per-product minimums before the create.
 */
export const buildBulkOfferingItems = (
  offeringProducts: BulkOfferingProducts = [],
  productIds?: string[],
): IQuoteItemReqSchema[] => {
  const selectedIds = getSelectedBulkProductIds(offeringProducts, productIds);
  return offeringProducts
    .filter(({ product }) => selectedIds.has(product.id))
    .map(({ product }, index) => ({
      productId: product.id,
      quantity: 0,
      position: index,
      customDiscountAmountOrPercent: null,
      ...(isNonProratedAdvanceProduct(product) && {
        incrementalQuantity: 0,
      }),
    }));
};

/**
 * Count line shown under an offering name in the review step, and whether the
 * user can choose products for it.
 */
export const getBulkProductsSummary = (
  offeringProducts: BulkOfferingProducts = [],
  productIds?: string[],
): { label: string; isChoosable: boolean } | null => {
  const totalCount = offeringProducts.length;
  if (!totalCount) {
    return null;
  }

  const requiredCount = offeringProducts.filter(
    ({ isMandatory }) => isMandatory,
  ).length;
  const optionalCount = totalCount - requiredCount;

  if (!optionalCount || totalCount === 1) {
    return {
      label: `${totalCount} ${pluralize('product', totalCount)}`,
      isChoosable: false,
    };
  }

  const selectedCount = getSelectedBulkProductIds(
    offeringProducts,
    productIds,
  ).size;

  if (!requiredCount) {
    return {
      label: `${selectedCount} of ${totalCount} products`,
      isChoosable: true,
    };
  }

  return {
    label: `${requiredCount} required, ${selectedCount - requiredCount} of ${optionalCount} optional`,
    isChoosable: true,
  };
};

const BILLING_FREQUENCY_PHRASES: Record<string, string> = {
  '1': 'monthly',
  '3': 'quarterly',
  '6': 'semi-annually',
  '12': 'annually',
};

/**
 * How a billing frequency reads inside a sentence, e.g. "billed monthly" or
 * "billed every 5 months".
 */
export const getBillingFrequencyPhrase = (billingFrequency: string): string => {
  if (BILLING_FREQUENCY_PHRASES[billingFrequency]) {
    return BILLING_FREQUENCY_PHRASES[billingFrequency];
  }
  const months = Number(billingFrequency);
  return `every ${months} ${pluralize('month', months)}`;
};

/**
 * Resolves each offering's rate for a billing frequency: the first rate billed
 * at that frequency, or `''` when there is none. One-time offerings always
 * take their first rate. Products already chosen for an offering are kept.
 */
export const resolveBulkRates = (
  offerings: IBulkOfferingItem[],
  billingFrequency: string,
  previousBulkRate: BulkRateEntry[] = [],
): BulkRateEntry[] => {
  const productIdsByOfferingId = new Map(
    previousBulkRate.map(({ offeringId, productIds }) => [
      offeringId,
      productIds,
    ]),
  );

  return offerings.map(({ id, type, rates }) => {
    const rate =
      type === OfferingTypesEnum.ONETIME
        ? rates?.[0]
        : rates?.find(
            ({ billingFrequencyInMonths }) =>
              !isNil(billingFrequencyInMonths) &&
              String(billingFrequencyInMonths) === billingFrequency,
          );
    const productIds = productIdsByOfferingId.get(id);

    return {
      offeringId: id,
      rateId: rate?.id ?? '',
      ...(productIds && { productIds }),
    };
  });
};

export const getCustomFrequencyError = (
  billingFrequency: string,
  customBillingFrequency: Maybe<number>,
): string | null => {
  if (billingFrequency === '0' && isNil(customBillingFrequency)) {
    return 'Custom value is required';
  }
  if (!isNil(customBillingFrequency) && customBillingFrequency < 1) {
    return 'Custom value cannot be less than 1';
  }
  return null;
};
